import React, { useMemo, useState } from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import { useSubmitPatientHistory } from '../../../../api/useSubmitPatientHistory'
import {
  FieldGrid,
  FieldSection,
  FormActions,
  LabeledDate,
  LabeledNumber,
  QuestionNumber,
  REQUIRED,
  noop,
  todayIso,
} from '../../../fieldPrimitives'
import { ValidationIssues } from '../validation'

type Props = {
  uid: string
  type: PatientHistoryType
  title: string
  // Prefixes every `{PREFIX}_QUESTION_N` / `{PREFIX}_SCORE` key in the submitted `value`.
  prefix: string
  questions: string[]
  max?: number
}

// No answer is the default: Standard's schema requires every question, so a skipped one must not save as 0.
const ScoreQuestionnaireFields = ({ uid, type, title, prefix, questions, max = 3 }: Props) => {
  const [date, setDate] = useState(todayIso())
  const [answers, setAnswers] = useState<(number | '')[]>(() => questions.map(() => ''))
  const [attempted, setAttempted] = useState(false)
  const [issues, setIssues] = useState<string[]>([])
  const { submit, submitting, error } = useSubmitPatientHistory(uid)

  const score = useMemo(() => answers.reduce<number>((sum, value) => sum + (value === '' ? 0 : value), 0), [answers])
  const invalid = (answer: number | '') => answer === '' || answer < 0 || answer > max

  const onSubmit = async () => {
    const invalidCount = answers.filter(invalid).length

    setAttempted(true)
    setIssues(
      invalidCount > 0 ? [`Answer every question from 0 to ${max} (${invalidCount} missing or out of range)`] : []
    )

    if (invalidCount > 0 || !date) {
      return
    }

    const value: Record<string, number> = { [`${prefix}_SCORE`]: score }
    answers.forEach((answer, index) => {
      value[`${prefix}_QUESTION_${index + 1}`] = Number(answer)
    })
    await submit({ date, type, value })
    setAnswers(questions.map(() => ''))
    setAttempted(false)
  }

  return (
    <FieldSection title={title}>
      <FieldGrid>
        <LabeledDate
          label="Date of event"
          value={date}
          onChange={setDate}
          required
          error={attempted && !date ? REQUIRED : undefined}
        />
      </FieldGrid>
      {questions.map((question, index) => (
        <QuestionNumber
          key={question}
          question={question}
          value={answers[index]}
          min={0}
          max={max}
          placeholder={`0-${max}`}
          required
          error={attempted && invalid(answers[index]) ? REQUIRED : undefined}
          onChange={(next) =>
            setAnswers((current) => current.map((value, position) => (position === index ? next : value)))
          }
        />
      ))}
      <FieldGrid>
        <LabeledNumber label="Score" value={score} disabled onChange={noop} />
      </FieldGrid>
      <ValidationIssues issues={issues} />
      <FormActions onSubmit={onSubmit} disabled={false} submitting={submitting} error={error} />
    </FieldSection>
  )
}

export default ScoreQuestionnaireFields
